import { type FC, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { type MetaAdsAudienceListItem, MetaAdsAudienceStatus } from "shared";
import { Select } from "../../components/Select";
import { useAudienceActions } from "../useAudiences";

export const ModalCreateLookalike: FC<{
	audiences: MetaAdsAudienceListItem[];
	onClose: () => void;
}> = ({ audiences, onClose }) => {
	const { createLookalike } = useAudienceActions();
	const [sourceAudienceId, setSourceAudienceId] = useState("");
	const [country, setCountry] = useState("US");
	const [percent, setPercent] = useState(1);

	const readyAudiences = useMemo(
		() => audiences.filter((a) => a.status === MetaAdsAudienceStatus.READY),
		[audiences],
	);

	const selectedSource = readyAudiences.find((a) => a.id === sourceAudienceId);
	const suggestedName = selectedSource
		? `Lookalike (${country}, ${percent}%) - ${selectedSource.name}`
		: "";
	const [name, setName] = useState("");
	const displayName = name || suggestedName;

	const handleSubmit = async () => {
		if (!sourceAudienceId || !displayName.trim()) return;
		await createLookalike.mutateAsync({
			name: displayName,
			sourceAudienceId,
			country,
			percent,
		});
		onClose();
	};

	const sizeLabel = (() => {
		if (percent <= 1) return "Closest match";
		if (percent <= 3) return "Balanced";
		if (percent <= 5) return "Broader reach";
		return "Broadest reach";
	})();

	return createPortal(
		<div
			className="modal d-block"
			role="dialog"
			style={{ background: "rgba(0,0,0,0.5)" }}
			onClick={(e) => e.target === e.currentTarget && onClose()}
			onKeyDown={(e) => e.key === "Escape" && onClose()}
		>
			<div className="modal-dialog modal-dialog-centered">
				<div className="modal-content">
					<div className="modal-header">
						<h5 className="modal-title">Create Lookalike Audience</h5>
						<button
							type="button"
							className="btn-close"
							onClick={onClose}
							aria-label="Close"
						/>
					</div>
					<div className="modal-body">
						<div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
							<div>
								<label className="form-label" htmlFor="lal-source">
									Source Audience
								</label>
								<Select
									id="lal-source"
									className="form-select"
									value={sourceAudienceId}
									onChange={(e) => {
										setSourceAudienceId(e.target.value);
										setName("");
									}}
								>
									<option value="">Select a source audience...</option>
									{readyAudiences.map((a) => (
										<option key={a.id} value={a.id}>
											{a.name} ({a.approximateSize.toLocaleString()} users)
										</option>
									))}
								</Select>
								{readyAudiences.length === 0 && (
									<small className="text-muted">
										No audiences with "Ready" status available. Create and
										populate a custom audience first.
									</small>
								)}
							</div>

							<div>
								<label className="form-label" htmlFor="lal-country">
									Target Country
								</label>
								<input
									id="lal-country"
									type="text"
									className="form-control"
									value={country}
									onChange={(e) =>
										setCountry(e.target.value.toUpperCase().slice(0, 2))
									}
									placeholder="US"
									maxLength={2}
								/>
							</div>

							<div>
								<label className="form-label" htmlFor="lal-percent">
									Audience Size: {percent}% - {sizeLabel}
								</label>
								<input
									id="lal-percent"
									type="range"
									className="form-range"
									min={1}
									max={10}
									step={1}
									value={percent}
									onChange={(e) => setPercent(Number(e.target.value))}
								/>
								<div
									style={{
										display: "flex",
										justifyContent: "space-between",
										fontSize: 12,
									}}
									className="text-muted"
								>
									<span>1% - Closest match</span>
									<span>10% - Broadest</span>
								</div>
								<small className="text-muted">
									For local businesses, 1-3% typically perform best.
								</small>
							</div>

							<div>
								<label className="form-label" htmlFor="lal-name">
									Audience Name
								</label>
								<input
									id="lal-name"
									type="text"
									className="form-control"
									value={name}
									onChange={(e) => setName(e.target.value)}
									placeholder={suggestedName || "Enter audience name"}
								/>
							</div>
						</div>
					</div>
					<div className="modal-footer">
						<button
							type="button"
							className="btn btn-outline-secondary"
							onClick={onClose}
						>
							Cancel
						</button>
						<button
							type="button"
							className="btn btn-primary"
							disabled={
								!sourceAudienceId ||
								!displayName.trim() ||
								createLookalike.isPending
							}
							onClick={handleSubmit}
						>
							{createLookalike.isPending ? "Creating..." : "Create Lookalike"}
						</button>
					</div>
				</div>
			</div>
		</div>,
		document.body,
	);
};
